import { ReactNode, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Collapse,
  Descriptions,
  Empty,
  Spin,
  Tag,
  Timeline,
  Tooltip,
} from 'antd';
import axios from 'axios';
import { NodeIndexOutlined } from '@ant-design/icons';
import { formatDateWithZone } from './format-date';
import { cadenceTraceUrl } from './trace-link';
import { useAuthenticatedRequest } from '../../UserContext';

export interface CadenceEvaluation {
  id: string;
  created_at: string;
  prompt_version?: string | null;
  stage?: string | null;
  outreach_channel?: string | null;
  next_check_in?: number | null;
  next_evaluation?: number | null;
  // Sub-day path: the resolved absolute wake and the minute offset it came from. Preferred over the
  // day-granular next_evaluation, which is null on these rows.
  next_evaluation_at?: string | null;
  next_evaluation_in_minutes?: number | null;
  rationale?: string | null;
  phoenix_trace_id?: string | null;
}

export interface CadenceEnrollment {
  campaign_key?: string | null;
  action_plan_name?: string;
  trigger_type?: string | null;
}

interface CadenceTimelineProps {
  accessToken: string;
  apiUrl: string;
  engagementId: string;
  enrollment: CadenceEnrollment;
  timezone: string;
}

const humanize = (value: string) => value.replace(/_/g, ' ');

// Stage as a temperature: warm presets for engaged/hot, cool for early/cold.
const STAGE_TAG: Record<string, string> = {
  hot: 'red',
  warm: 'orange',
  engaged: 'blue',
  nurture: 'green',
  new: 'cyan',
  cold: 'geekblue',
};

const actionColor = (action: string) =>
  action.startsWith('Send')
    ? '#389e0d'
    : action.startsWith('Re-evaluate')
      ? '#1677ff'
      : '#8c8c8c';

const rationalePreview = (value: string): string => {
  const compact = value.replace(/\s+/g, ' ').trim();
  return compact.length > 240 ? `${compact.slice(0, 239).trimEnd()}…` : compact;
};

const dayLabel = (days: number) => (days === 0 ? 'today' : `${days}d`);

// Raw minutes are unreadable past an hour ("1211 min"); render as a human duration.
// Exact decomposition (no rounding), so minute precision survives and hours never hit 24.
const humanMinutes = (mins: number): string => {
  if (mins < 60) {
    return `${mins} min`;
  }
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  return [d && `${d}d`, h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ');
};

// The decision as concrete actions: what the engine chose to send and when, then when it re-evaluates.
const scheduleActions = (e: CadenceEvaluation, timezone: string): string[] => {
  const actions: string[] = [];

  if (e.outreach_channel === 'none') {
    actions.push('Hold — no outreach this cycle');
  } else if (e.next_check_in != null) {
    // next_check_in null = the scheduler skips the contact this cycle (0 = same-day). A channel
    // without a scheduled contact isn't a send now; it fires when the re-evaluation comes due.
    const when = e.next_check_in === 0 ? ' now' : ` in ${e.next_check_in}d`;
    if (
      e.outreach_channel === 'sms' ||
      e.outreach_channel === 'sms_and_phone_call'
    ) {
      actions.push(`Send SMS${when}`);
    }
    if (
      e.outreach_channel === 'phone_call' ||
      e.outreach_channel === 'sms_and_phone_call'
    ) {
      actions.push(`Send call${when}`);
    }
  }

  if (e.next_evaluation_at) {
    const rel =
      e.next_evaluation_in_minutes != null
        ? ` (in ${humanMinutes(e.next_evaluation_in_minutes)})`
        : '';
    actions.push(
      `Re-evaluate ${formatDateWithZone(e.next_evaluation_at, timezone)}${rel}`,
    );
  } else if (e.next_evaluation_in_minutes != null) {
    // Minutes set but the absolute column wasn't resolved — show the offset once.
    actions.push(
      `Re-evaluate in ${humanMinutes(e.next_evaluation_in_minutes)}`,
    );
  } else if (e.next_evaluation != null) {
    actions.push(`Re-evaluate in ${dayLabel(e.next_evaluation)}`);
  }

  return actions;
};

interface CadenceEvaluationRowProps {
  actionOutcomes?: {
    call?: ReactNode;
    sms?: ReactNode;
  };
  defaultExpanded?: boolean;
  embedded?: boolean;
  evaluation: CadenceEvaluation;
  isLegacyEnrollment: boolean;
  showDivider?: boolean;
  timezone: string;
}

export const CadenceEvaluationRow = ({
  actionOutcomes,
  defaultExpanded = false,
  embedded = false,
  evaluation,
  isLegacyEnrollment,
  showDivider = false,
  timezone,
}: CadenceEvaluationRowProps) => {
  const actions = scheduleActions(evaluation, timezone);
  const traceUrl = evaluation.phoenix_trace_id
    ? cadenceTraceUrl(evaluation.phoenix_trace_id)
    : null;
  return (
    <div
      style={{
        borderBottom: showDivider ? '1px solid #e8e8e8' : 'none',
        marginBottom: showDivider ? 4 : 0,
        padding: embedded ? '0 0 8px' : '0 12px 8px',
      }}
    >
      {!embedded ? (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'flex-end',
            marginBottom: 4,
          }}
        >
          <Tooltip title={timezone}>
            <span
              style={{
                color: '#737373',
                fontSize: 12,
                whiteSpace: 'nowrap',
              }}
            >
              {formatDateWithZone(evaluation.created_at, timezone)}
            </span>
          </Tooltip>
          {traceUrl ? (
            <Button
              href={traceUrl}
              icon={<NodeIndexOutlined aria-hidden="true" />}
              rel="noopener noreferrer"
              size="small"
              target="_blank"
            >
              View in Phoenix
            </Button>
          ) : null}
        </div>
      ) : null}
      <Collapse
        bordered={false}
        className={
          embedded ? 'engagement-timeline-contained-collapse' : undefined
        }
        defaultActiveKey={defaultExpanded ? ['decision'] : undefined}
        expandIconPosition="end"
        items={[
          {
            key: 'decision',
            label: (
              <div
                style={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  flex: 1,
                  gap: 8,
                  justifyContent: 'space-between',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flex: 1,
                    flexWrap: 'wrap',
                    gap: 8,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  {evaluation.stage ? (
                    <Tag
                      color={STAGE_TAG[evaluation.stage] || 'default'}
                      style={{
                        fontWeight: 500,
                        margin: 0,
                        textTransform: 'capitalize',
                      }}
                    >
                      {humanize(evaluation.stage)}
                    </Tag>
                  ) : null}
                  <div
                    style={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'column',
                      gap: 2,
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {actions.length ? (
                      actions.map(action => (
                        <div
                          key={action}
                          style={{
                            alignItems: 'baseline',
                            display: 'flex',
                            flexWrap: 'wrap',
                            fontSize: 13,
                            fontWeight: 500,
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              color: actionColor(action),
                              fontWeight: 700,
                            }}
                          >
                            →
                          </span>
                          <span style={{ color: '#262626' }}>{action}</span>
                        </div>
                      ))
                    ) : embedded && evaluation.rationale ? null : (
                      <span style={{ color: '#8c8c8c', fontSize: 13 }}>
                        Evaluation details
                      </span>
                    )}
                    {embedded && evaluation.rationale ? (
                      <div
                        aria-hidden="true"
                        style={{
                          color: '#737373',
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 400,
                          marginTop: 2,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>
                          Why this decision:
                        </span>{' '}
                        {rationalePreview(evaluation.rationale)}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ),
            children: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  {evaluation.prompt_version ? (
                    <span
                      style={{
                        background: '#f0f0f0',
                        borderRadius: 4,
                        color: '#737373',
                        fontFamily: 'monospace',
                        fontSize: 12,
                        padding: '1px 6px',
                      }}
                    >
                      {evaluation.prompt_version}
                    </span>
                  ) : null}
                  {isLegacyEnrollment ? (
                    <Tooltip title="No campaign assigned to this engagement">
                      <Tag
                        color="default"
                        style={{ borderStyle: 'dashed', margin: 0 }}
                      >
                        legacy
                      </Tag>
                    </Tooltip>
                  ) : null}
                </div>
                {evaluation.rationale ? (
                  <div>
                    <div
                      style={{
                        color: '#8c8c8c',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.4,
                        marginBottom: 4,
                        textTransform: 'uppercase',
                      }}
                    >
                      Why this decision
                    </div>
                    <div
                      style={{
                        color: '#595959',
                        fontSize: 14,
                        lineHeight: 1.6,
                      }}
                    >
                      {evaluation.rationale}
                    </div>
                  </div>
                ) : null}
              </div>
            ),
          },
        ]}
        size="small"
        style={{
          background: embedded ? 'transparent' : '#fafafa',
          borderRadius: embedded ? 0 : 8,
        }}
      />
      {embedded && (actionOutcomes?.sms || actionOutcomes?.call) ? (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            padding: '2px 12px 0',
          }}
        >
          {actionOutcomes.sms ? (
            <span style={{ alignItems: 'center', display: 'flex', gap: 4 }}>
              <span style={{ color: '#737373', fontSize: 12 }}>SMS</span>
              {actionOutcomes.sms}
            </span>
          ) : null}
          {actionOutcomes.call ? (
            <span style={{ alignItems: 'center', display: 'flex', gap: 4 }}>
              <span style={{ color: '#737373', fontSize: 12 }}>Call</span>
              {actionOutcomes.call}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const CadenceTimeline = ({
  accessToken,
  apiUrl,
  engagementId,
  enrollment,
  timezone,
}: CadenceTimelineProps) => {
  const authenticatedRequest = useAuthenticatedRequest(accessToken);
  const isLegacyEnrollment = !enrollment.campaign_key;
  const [evaluations, setEvaluations] = useState<CadenceEvaluation[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchEvaluations = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const response = await authenticatedRequest(headers =>
          axios.get<CadenceEvaluation[]>(
            `${apiUrl}/api/lead-engagement/${engagementId}/cadence-evaluations`,
            { headers },
          ),
        );
        if (!cancelled) {
          setEvaluations(response.data);
        }
      } catch {
        if (!cancelled) {
          setEvaluations([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void fetchEvaluations();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, engagementId, accessToken]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card size="small" title="Enrollment">
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="Campaign">
            {enrollment.campaign_key ? (
              <Tag color="geekblue">{humanize(enrollment.campaign_key)}</Tag>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Action Plan">
            {enrollment.action_plan_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Trigger">
            {enrollment.trigger_type ? humanize(enrollment.trigger_type) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        size="small"
        title={`Decision History${evaluations.length ? ` (${evaluations.length})` : ''}`}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin />
          </div>
        ) : evaluations.length === 0 ? (
          <Empty
            description={
              failed
                ? 'Decision history is not available'
                : 'The engine has not evaluated this lead yet'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Timeline
            items={evaluations.map((evaluation, i) => ({
              color: evaluation.outreach_channel === 'none' ? 'gray' : 'blue',
              children: (
                <CadenceEvaluationRow
                  defaultExpanded
                  evaluation={evaluation}
                  isLegacyEnrollment={isLegacyEnrollment}
                  showDivider={i < evaluations.length - 1}
                  timezone={timezone}
                />
              ),
            }))}
          />
        )}
      </Card>
    </div>
  );
};
